import { ElementType } from "react";
import type { LoadingType } from "react-loading";
import { Breakpoints, ButtonProps } from "@mui/material";
import { createTheme, Theme, adaptV4Theme, PaletteColorOptions } from "@mui/material/styles";

import {
	commonColors,
	dangerColors,
	getShades,
	greyColors,
	legacyColors,
	primaryColors,
	secondaryColors,
	successColors,
	warningColors,
} from "./colors";
import { componentOverrides } from "./overrides";

export interface ThemeModel {
	color: {
		primary: { main: string; light: string };
		secondary: { main: string };
	};
	desktop?: {
		fileTextColor: string;
	};
}

interface OrganizationModel {
	name: string;
	parentId?: string;
	logoURI: string;
	backgroundURI: string;
	description?: string;
	theme: ThemeModel;
	slug: string;
}

// todo: migrate from v4 by getting rid of all makeStyles and delete this.
declare module "@mui/styles" {
	interface DefaultTheme extends Theme {
		organization: Pick<OrganizationModel, "name" | "logoURI" | "backgroundURI">;
		desktop: ThemeModel["desktop"];
	}

	interface BreakpointOverrides {
		xs: true;
		sm: true;
		md: true;
		lg: true;
		xl: true;
		mobile: true;
		smallMobile: true;
		desktop: true;
	}
}
// todo: migrate from v4 by getting rid of all makeStyles and delete this.
declare module "@mui/material" {
	interface DefaultTheme extends Theme {
		organization: Pick<OrganizationModel, "name" | "logoURI" | "backgroundURI" | "theme">;
		desktop: ThemeModel["desktop"];
	}

	interface DeprecatedThemeOptions {
		organization: Partial<Pick<OrganizationModel, "name" | "logoURI" | "backgroundURI">>;
		desktop: ThemeModel["desktop"];
	}
}

// * Keep these ones.
declare module "@mui/material/styles" {
	interface Theme {
		organization: Pick<OrganizationModel, "name" | "logoURI" | "backgroundURI">;
		desktop: ThemeModel["desktop"];
	}

	interface BreakpointOverrides {
		xs: true;
		sm: true;
		md: true;
		lg: true;
		xl: true;
		mobile: true;
		smallMobile: true;
		desktop: true;
	}

	interface ThemeOptions {
		organization?: Partial<Pick<OrganizationModel, "name" | "logoURI" | "backgroundURI">>;
		desktop?: ThemeModel["desktop"];
	}

	interface Palette {
		legacyPrimary: PaletteColorOptions;
		legacySecondary: PaletteColorOptions;
		legacyWarning: PaletteColorOptions;
		legacySuccess: PaletteColorOptions;
		danger: PaletteColorOptions;
	}

	interface PaletteOptions {
		legacyPrimary?: PaletteColorOptions;
		legacySecondary?: PaletteColorOptions;
		legacyWarning?: PaletteColorOptions;
		legacySuccess?: PaletteColorOptions;
		danger?: PaletteColorOptions;
	}
}

declare module "@mui/material/Button" {
	interface ExtendedButtonProps extends ButtonProps {
		"data-useremunit"?: "true" | "false";
		loading?: boolean;
		loadingType?: LoadingType;
		component?: ElementType;
	}

	interface ButtonPropsVariantOverrides {
		approve: true;
		reject: true;
		complete: true;
	}

	interface ButtonPropsColorOverrides {
		danger: true;
	}

	// note: To add autocompletion in the styleOverrides of MuiButton.
	interface ButtonClasses {
		approve: string;
		reject: string;
		complete: string;
	}
}

declare module "@mui/material/Chip" {
	interface ChipPropsColorOverrides {
		legacySuccess: true;
	}
}

const breakpoints:
	| Partial<
			{
				unit: string;
				step: number;
			} & Breakpoints
	  >
	| undefined = {
	values: { mobile: 435, desktop: 960, smallMobile: 300, xs: 0, sm: 600, md: 900, lg: 1200, xl: 1536 },
	keys: ["xs", "md", "lg", "xl", "mobile", "desktop"],
	down(key) {
		// @ts-ignore
		return `@media (max-width:${this.values[key] - 0.5}px)`;
	},
};

export const ENBOQ_LOGO =
	"https://riddlestory-common-assets.s3.eu-central-1.amazonaws.com/logo/Enboq-Logo-NoPayoff-Svg.svg";
export const ENBOQ_BACKGROUND =
	"https://riddlestory-common-assets.s3.eu-central-1.amazonaws.com/logo/enboq-background2.png";

export const enboqTheme = (htmlFontSize?: number) => {
	const theme = createTheme(
		adaptV4Theme({
			organization: {
				name: "Enboq",
				logoURI: ENBOQ_LOGO,
				backgroundURI: ENBOQ_BACKGROUND,
			},
			desktop: undefined,
			palette: {
				primary: primaryColors,
				secondary: secondaryColors,
				warning: warningColors,
				success: successColors,
				common: commonColors,
				grey: greyColors,
				legacyPrimary: legacyColors.primary,
				legacySecondary: legacyColors.secondary,
				legacyWarning: legacyColors.warning,
				legacySuccess: legacyColors.success,
				danger: dangerColors,
				tonalOffset: 0.2,
				contrastThreshold: 3,
			},
			typography: {
				htmlFontSize,
				allVariants: {
					color: greyColors["700"],
				},
				fontFamily: "Nunito",
				button: {
					textTransform: "none",
					fontWeight: 500,
				},
			},
			breakpoints,
		}),
		componentOverrides,
	);

	return theme;
};

export const createCustomTheme = (options: OrganizationModel, htmlFontSize?: number) => {
	const customTheme = createTheme(
		adaptV4Theme({
			organization: {
				name: options.name,
				logoURI: options.logoURI || ENBOQ_LOGO,
				backgroundURI: options.backgroundURI,
			},
			desktop: options.theme.desktop,
			palette: {
				primary: {
					...options.theme.color.primary,
					...getShades(options.theme.color.primary.main),
				},
				secondary: {
					...options.theme.color.secondary,
					...getShades(options.theme.color.secondary.main),
				},
				warning: warningColors,
				success: successColors,
				common: commonColors,
				grey: greyColors,
				legacyPrimary: legacyColors.primary,
				legacySecondary: legacyColors.secondary,
				legacyWarning: legacyColors.warning,
				legacySuccess: legacyColors.success,
				danger: dangerColors,
				tonalOffset: 0.2,
				contrastThreshold: 3,
			},
			typography: {
				htmlFontSize,
				allVariants: {
					color: greyColors["700"],
				},
				fontFamily: "Nunito",
				button: {
					textTransform: "none",
					fontWeight: 500,
				},
			},
			breakpoints,
		}),
		componentOverrides,
	);

	return customTheme;
};
